/** Small presentation helpers for the message list and reader. */
import type { Envelope, MailAddress, RemoteContentDefault } from "../shared/types";

/**
 * Pure predicate driving the Reader's "Remote content was blocked" banner.
 * Extracted from the JSX condition so it is testable without a DOM render
 * (Extract-Before-Mock). The banner shows ONLY when the message actually
 * embeds remote content, the user has NOT already revealed it, and the account
 * default is not `always` (an `always` account never needs the prompt).
 */
export function shouldShowRemoteBanner(
  hasRemoteContent: boolean,
  showRemote: boolean,
  remoteContentDefault: RemoteContentDefault,
): boolean {
  return hasRemoteContent && !showRemote && remoteContentDefault !== "always";
}

/**
 * Extract the registrable domain (eTLD+1) from a hostname.
 * mail.anthropic.com → anthropic.com
 * github.com → github.com
 * Works for common 2-part TLDs (co.uk, com.br, etc.) heuristically.
 */
function rootDomain(hostname: string): string {
  const parts = hostname.split(".");
  if (parts.length <= 2) return hostname;
  // Detect known 2-part TLDs like co.uk, com.br, org.ar, net.au …
  const twoPartTld = /^(co|com|net|org|gov|edu|ac|or|ne)\.[a-z]{2}$/i;
  if (parts.length >= 3 && twoPartTld.test(parts.slice(-2).join("."))) {
    return parts.slice(-3).join(".");
  }
  return parts.slice(-2).join(".");
}

/** Build the proxied favicon URL for a sender domain extracted from an email address.
 *  Always resolves to the registrable root domain so subdomains (mail.*, no-reply.*)
 *  map to the same favicon as the main brand. */
export function faviconUrl(emailAddress: string): string | null {
  if (!emailAddress) return null;
  const at = emailAddress.lastIndexOf("@");
  if (at < 0) return null;
  const raw = emailAddress.slice(at + 1).toLowerCase().trim();
  if (!raw || !raw.includes(".")) return null;
  const domain = rootDomain(raw);
  return `/api/favicon?domain=${encodeURIComponent(domain)}`;
}

export function displayName(addr?: MailAddress): string {
  if (!addr) return "Unknown";
  return addr.name?.trim() || addr.address || "Unknown";
}

export function senderLabel(env: Envelope): string {
  return displayName(env.from[0]);
}

export function initials(label: string): string {
  const parts = label.replace(/[<>"]/g, "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

const AVATAR_COLORS = [
  "#3B82F6", // accent-anchored blue first
  "#06B6D4", // cyan
  "#8B5CF6", // violet
  "#EC4899", // pink
  "#10B981", // emerald
  "#F59E0B", // amber
  "#EF4444", // red
  "#14B8A6", // teal
];

export function avatarColor(seed: string): string {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length]!;
}

function startOfDay(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

/** Proton-like compact date: time today, "Yesterday", weekday this week, else date. */
export function formatListDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const now = new Date();
  if (d.toDateString() === now.toDateString()) {
    return d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  }
  const diffDays = Math.floor((startOfDay(now) - startOfDay(d)) / 86_400_000);
  if (diffDays === 1) return "Yesterday";
  if (diffDays > 1 && diffDays < 7) return d.toLocaleDateString(undefined, { weekday: "short" });
  const sameYear = d.getFullYear() === now.getFullYear();
  return d.toLocaleDateString(
    undefined,
    sameYear ? { day: "numeric", month: "short" } : { day: "numeric", month: "short", year: "numeric" },
  );
}
